/**
 * COMPLETE DATA TASK BUTTON
 *
 * Button component for data team to mark data configuration tasks as complete.
 * Creates FINAL_ACTIVATION task when first location becomes ready for activation.
 * For single-location adds (org already activated), directly calls activate-location.
 */

'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle } from 'lucide-react';
import type { Task, Location } from '@/types/database';
import { updateLocationStatuses, processActivation } from '@/lib/onboarding-status';

interface CompleteDataTaskButtonProps {
  task: Task;
  location: Location | null;
  userId: string;
}

export function CompleteDataTaskButton({
  task,
  location,
  userId,
}: CompleteDataTaskButtonProps) {
  const navigate = useNavigate();
  const supabase = createClient();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCompleted = task.status === 'COMPLETED';

  const handleComplete = async () => {
    try {
      setLoading(true);
      setError(null);

      // Mark task as complete
      const { error: taskError } = await supabase
        .from('tasks')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
          completed_by: userId,
        })
        .eq('id', task.id);

      if (taskError) throw taskError;

      // Get the current organization status to send to main app
      const { data: org } = await supabase
        .from('organizations')
        .select('status')
        .eq('id', task.organization_id)
        .single();

      // Determine if this is a single-location add by comparing original vs current location count
      // Get the original number of locations from onboarding_queue
      const { data: onboardingQueue } = await supabase
        .from('onboarding_queue')
        .select('number_of_locations')
        .eq('id', task.organization_id)
        .single();

      // Get the current count of locations for this organization
      const { count: currentLocationCount } = await supabase
        .from('locations')
        .select('id', { count: 'exact' })
        .eq('organization_id', task.organization_id);

      const originalLocationCount = onboardingQueue?.number_of_locations || 0;
      const actualLocationCount = currentLocationCount || 0;

      // If current locations > original locations, this is a single-location add
      const isSingleLocationAdd = actualLocationCount > originalLocationCount;

      if (isSingleLocationAdd) {
        // For single location add: Call activate-location directly
        console.log('Detected single-location add flow, activating location immediately');
        
        if (!location?.id) {
          throw new Error('Location ID is required for activation');
        }

        try {
          await processActivation(task.organization_id, location.id, userId);
          console.log('Location activated successfully via process-activation');
        } catch (activationError) {
          console.error('Error activating location:', activationError);
          setError(
            activationError instanceof Error 
              ? `Task completed but activation failed: ${activationError.message}` 
              : 'Task completed but activation failed'
          );
          setLoading(false);
          return;
        }
      } else {
        // For full organization onboarding: Update status and create FINAL_ACTIVATION task when location is ready
        // Update this location's status in the main app
        if (location?.id && org?.status) {
          await updateLocationStatuses(
            task.organization_id,
            org.status,
            [{
              id: location.id,
              status: 'AWAITING_ACTIVATION',
            }]
          );
        }

        // Create FINAL_ACTIVATION task if it doesn't exist yet
        // This allows locations to be activated individually as they become ready
        const { data: existingActivation } = await supabase
          .from('tasks')
          .select('id')
          .eq('organization_id', task.organization_id)
          .eq('type', 'FINAL_ACTIVATION')
          .maybeSingle();

        if (!existingActivation) {
          const { error: activationError } = await supabase
            .from('tasks')
            .insert({
              organization_id: task.organization_id,
              location_id: null, // Organization-level task, not per-location
              type: 'FINAL_ACTIVATION',
              title: `Final Activation`,
              description: `Final activation and review for organization - locations can be activated individually as they become ready`,
              status: 'PENDING',
              priority: 1,
              sequence_order: 3,
            });

          if (activationError) {
            console.error('Failed to create FINAL_ACTIVATION task:', activationError);
            // Don't throw - this is not critical for the flow
          }
        }

        // Check if all Data tasks for this organization are complete
        const { data: orgTasks } = await supabase
          .from('tasks')
          .select('id, status')
          .eq('organization_id', task.organization_id)
          .eq('type', 'DATA_CONFIGURATION');

        const allComplete = orgTasks?.every(
          (t) => t.id === task.id || t.status === 'COMPLETED'
        ) && org?.status !== 'ACTIVATED';

        // If all Data tasks are complete, update organization status
        if (allComplete) {
          await supabase
            .from('organizations')
            .update({
              status: 'FINAL_REVIEW',
            })
            .eq('id', task.organization_id);

          // Update all locations to reflect org status change via edge function
          const { data: locations } = await supabase
            .from('locations')
            .select('id')
            .eq('organization_id', task.organization_id);

          if (locations && locations.length > 0) {
            await updateLocationStatuses(
              task.organization_id,
              'FINAL_REVIEW',
              locations.map((loc: any) => ({
                id: loc.id,
                status: 'AWAITING_ACTIVATION',
              }))
            );
          }
        }
      }

      // Log task completion - wrapped in try-catch so it doesn't block the flow
      try {
        await supabase.rpc('log_activity', {
          p_user_id: userId,
          p_action: 'TASK_COMPLETED',
          p_description: `Data configuration task completed for ${location?.name}`,
          p_organization_id: task.organization_id,
          p_task_id: task.id,
          p_location_id: location?.id,
        });
      } catch (logError) {
        console.error('Failed to log activity:', logError);
        // Don't block the flow if logging fails
      }

      navigate('/data-tasks');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete task');
      setLoading(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <CheckCircle className="h-4 w-4" />
        Task Completed
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 p-2 rounded">
          {error}
        </div>
      )}
      <Button onClick={handleComplete} disabled={loading} className="w-full">
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Completing...
          </>
        ) : (
          'Complete Task'
        )}
      </Button>
    </div>
  );
}
